import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faPen, faPlus, faFilter, faChevronDown, faChevronUp, faFilePdf, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import ColumnVisibilityModal from '../../components/ColumnVisibilityModal';
import { Link, useSearchParams } from 'react-router-dom';
import Table from '../../components/Table';
import Pagination from '../../components/Pagination';
import { fetchAlunos } from '../../services/alunoService';
import * as XLSX from 'xlsx';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import cursoService from '../../services/cursoService';
import { extensaoService } from '../../services/extensaoService';
import { useAuth } from '../../contexts/AuthContext';
import { useDebounce } from '../../hooks/useDebounce';
import Cookies from 'js-cookie';

const columns = [
  { Header: 'Matrícula', accessor: 'matricula' },
  { Header: 'Situação', accessor: 'status' },
  { Header: 'Nome', accessor: 'nome' },
  { Header: 'CPF', accessor: 'cpf' },
  { Header: 'Email', accessor: 'email' },
  { Header: 'Data de Nascimento', accessor: 'dataNascimento', Cell: value => formatDate(value) },
  { Header: 'Telefone Celular', accessor: 'telefoneCelular' },
  { Header: 'Telefone Residencial', accessor: 'telefoneResidencial' },
  { Header: 'Bairro', accessor: 'bairro' },
  { Header: 'Cidade', accessor: 'cidade' },
  { Header: 'Estado', accessor: 'estado' },
  { Header: 'Escolaridade', accessor: 'escolaridade' },
  { Header: 'Igreja', accessor: 'nomeIgrejaCongrega' },
  { Header: 'Cargo', accessor: 'cargo' },
  { Header: 'Tipo Curso', accessor: 'tipoCurso' },
  { Header: 'Período', accessor: 'periodo' },
  { Header: 'Data Matrícula', accessor: 'dataMatricula', Cell: value => formatDate(value) },
  { Header: 'Cadastrado Por', accessor: 'cadastradoPor' },
  { Header: 'Alterado Por', accessor: 'alteradoPor' },
  { Header: 'Alterado Em', accessor: 'alteradoEm', Cell: value => formatDateTime(value) },
];

const pageSizes = [5, 10, 20, 50, 100, 200, 500, 1000];

const AlunosList = () => {
  const { user } = useAuth();
  // Permite chegar nesta tela com filtros pré-aplicados via querystring,
  // ex.: /alunos?status=Ativo (drill-down do Dashboard Admin)
  const [searchParams] = useSearchParams();

  const [filters, setFilters] = useState({
    nome: searchParams.get('nome') || '',
    cpf: searchParams.get('cpf') || '',
    matricula: searchParams.get('matricula') || '',
    curso: searchParams.get('curso') || '',
    extensoes: searchParams.get('extensoes') || '',
    status: searchParams.get('status') || '',
    // Filtro semântico vindo do alerta "Alunos sem matrícula ativa"
    // do Dashboard Admin. Encapsula todas as regras do alerta.
    semMatriculaAtiva: searchParams.get('semMatriculaAtiva') || '',
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Filtrar colunas baseado no nível de acesso
  const colunasAuditoria = ['cadastradoPor', 'alteradoPor', 'alteradoEm'];
  
  const nivelAcesso = user?.nivelAcesso || user?.perfil?.nivelAcesso || user?.nivel_acesso || 0;
  
  const colunasDisponiveis = nivelAcesso >= 5 
    ? columns 
    : columns.filter(col => !colunasAuditoria.includes(col.accessor));
  
  const [visibleColumns, setVisibleColumns] = useState(colunasDisponiveis.map(col => col.accessor));
  const [alunos, setAlunos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [loadingExcel, setLoadingExcel] = useState(false);
  const [error, setError] = useState(null);
  const [cursos, setCursos] = useState([]);
  const [extensoes, setExtensoes] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const [totalPagesServer, setTotalPagesServer] = useState(1);
  const [totalServer, setTotalServer] = useState(0);

  // Carregar cursos e extensões ao montar o componente
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [cursosData, extensoesData] = await Promise.all([
          cursoService.list({ limit: 1000 }),
          extensaoService.list({ limit: 1000 })
        ]);
        
        // Cursos - pode vir com .success ou direto
        const cursosArray = cursosData.success 
          ? cursosData.data 
          : (cursosData.data || cursosData);
        if (Array.isArray(cursosArray)) {
          setCursos(cursosArray);
        }
        
        // Extensões - pode vir com .success ou direto
        const extensoesArray = extensoesData.success 
          ? extensoesData.data 
          : (extensoesData.data || extensoesData);
        if (Array.isArray(extensoesArray)) {
          setExtensoes(extensoesArray);
        }
      } catch (error) {
        console.error('Erro ao carregar opções:', error);
      }
    };
    
    loadOptions();
  }, []);

  // Os filtros só chegam à API depois que o usuário para de digitar: antes,
  // cada tecla disparava uma consulta na base inteira (22 mil alunos) e a
  // resposta de uma busca antiga podia sobrescrever a mais recente.
  const filtrosComAtraso = useDebounce(filters, 400);

  useEffect(() => {
    let cancelado = false;
    const getAlunos = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchAlunos({ page, limit: pageSize, filters: filtrosComAtraso });
        if (cancelado) return;
        
        if (result.success) {
          const alunosData = Array.isArray(result.data) ? result.data : result.data?.data || result.data?.pessoas || [];
          setAlunos(alunosData);
          setTotalPagesServer(result.pagination?.totalPages || 1);
          setTotalServer(result.pagination?.total || 0);
        } else {
          setError(result.error || 'Erro ao carregar alunos');
          setAlunos([]);
        }
      } catch (err) {
        if (cancelado) return;
        setError(err.message || 'Erro inesperado ao carregar alunos');
        setAlunos([]);
      } finally {
        if (!cancelado) setLoading(false);
      }
    };
    getAlunos();
    // Descarta o resultado se outra busca começou antes desta terminar.
    return () => { cancelado = true; };
  }, [page, pageSize, filtrosComAtraso]);

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);
  const handleToggleColumn = (accessor) => {
    setVisibleColumns(prev =>
      prev.includes(accessor)
        ? prev.filter(col => col !== accessor)
        : [...prev, accessor]
    );
  };

  // With server-side pagination/filtering, use server totals
  const totalPages = totalPagesServer;
  const paginatedAlunos = alunos;

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  const handleGerarRelatorio = async () => {
    setLoadingPdf(true);
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params.append(key, filters[key]);
        }
      });
      
      const token = Cookies.get('auth_token') || localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('Token de autenticação não encontrado. Faça login novamente.');
      }
      
      // Determinar URL da API baseado no ambiente (mesma lógica do api.js)
      let baseUrl;
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      } else {
        baseUrl = 'https://api.ibe.elshadaysystems.com.br/api';
      }
      
      const url = `${baseUrl}/alunos/relatorio?${params.toString()}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('📄 Erro na resposta:', errorText);
        throw new Error(`Erro ao gerar relatório: ${response.status} - ${errorText}`);
      }
      
      const blob = await response.blob();
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `relatorio-alunos-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      alert(`Erro ao gerar relatório PDF: ${error.message}`);
    } finally {
      setLoadingPdf(false);
    }
  };

  const handleExportarExcel = () => {
    if (!alunos || alunos.length === 0) {
      alert('Não há dados para exportar.');
      return;
    }
    const colunasVisiveis = columns.filter(col => visibleColumns.includes(col.accessor));
    const linhas = alunos.map(aluno => {
      const linha = {};
      colunasVisiveis.forEach(col => {
        const valor = aluno[col.accessor];
        linha[col.Header] = col.Cell ? col.Cell(valor) : (valor ?? '');
      });
      return linha;
    });
    const ws = XLSX.utils.json_to_sheet(linhas);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Alunos');
    XLSX.writeFile(wb, `alunos-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <div className="flex-1 w-full max-w-full overflow-x-hidden">
      <div className="px-2 sm:px-6">
        <div className="max-w-full">
          {/* Cabeçalho responsivo */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
            <h2 className="text-lg sm:text-2xl font-bold text-gray-800">Listagem de Alunos</h2>
            <div className="flex gap-2">
              <button
                onClick={handleExportarExcel}
                disabled={loadingExcel}
                className={`px-3 py-2 sm:px-4 sm:py-2 rounded ${loadingExcel ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-700 hover:bg-green-800'} text-white font-semibold shadow focus:outline-none focus:ring-2 focus:ring-green-400 flex items-center justify-center gap-2 text-sm sm:text-base`}
              >
                <FontAwesomeIcon icon={faFileExcel} className={loadingExcel ? 'animate-pulse' : ''} />
                <span>{loadingExcel ? 'Exportando...' : 'Exportar Excel'}</span>
              </button>
              <button
                onClick={handleGerarRelatorio}
                disabled={loadingPdf}
                className={`px-3 py-2 sm:px-4 sm:py-2 rounded ${loadingPdf ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'} text-white font-semibold shadow focus:outline-none focus:ring-2 focus:ring-red-400 flex items-center justify-center gap-2 text-sm sm:text-base`}
              >
                <FontAwesomeIcon icon={faFilePdf} className={loadingPdf ? 'animate-pulse' : ''} />
                <span>{loadingPdf ? 'Gerando PDF...' : 'Relatório PDF'}</span>
              </button>
              <Link
                to="/alunos/create"
                className="px-3 py-2 sm:px-4 sm:py-2 rounded bg-green-600 text-white font-semibold shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 flex items-center justify-center gap-2 text-sm sm:text-base"
              >
                <FontAwesomeIcon icon={faPlus} />
                <span>Novo Aluno</span>
              </Link>
            </div>
          </div>
          <ColumnVisibilityModal
            columns={colunasDisponiveis}
            visibleColumns={visibleColumns}
            onChange={handleToggleColumn}
            onClose={handleCloseModal}
            isOpen={isModalOpen}
          />
          
          {/* Botão para mostrar/ocultar filtros */}
          <div className="mb-3">
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-400 transition-colors text-sm"
            >
              <FontAwesomeIcon icon={faFilter} />
              <span>{showFilters ? 'Ocultar Filtros' : 'Exibir Filtros'}</span>
              <FontAwesomeIcon icon={showFilters ? faChevronUp : faChevronDown} className="text-xs" />
            </button>
          </div>

          {/* Área de filtros - colapsável */}
          {showFilters && (
            <div className="max-w-full mb-4 p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
                <input 
                  type="text" 
                  name="nome" 
                  value={filters.nome} 
                  onChange={handleFilterChange} 
                  placeholder="Filtrar por nome" 
                  className="w-full sm:w-auto px-2 py-1.5 text-sm rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400" 
                />
                <input 
                  type="text" 
                  name="cpf" 
                  value={filters.cpf} 
                  onChange={handleFilterChange} 
                  placeholder="Filtrar por CPF" 
                  className="w-full sm:w-auto px-2 py-1.5 text-sm rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400" 
                />
                <input 
                  type="text" 
                  name="matricula" 
                  value={filters.matricula} 
                  onChange={handleFilterChange} 
                  placeholder="Filtrar por Matrícula" 
                  className="w-full sm:w-auto px-2 py-1.5 text-sm rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400" 
                />
                <select 
                  name="curso" 
                  value={filters.curso} 
                  onChange={handleFilterChange} 
                  className="w-full sm:w-auto px-2 py-1.5 text-sm rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
                >
                  <option value="">Todos os Cursos</option>
                  {cursos.map(curso => (
                    <option key={curso.idCurso} value={curso.idCurso}>{curso.nome}</option>
                  ))}
                </select>
                <select 
                  name="extensoes" 
                  value={filters.extensoes} 
                  onChange={handleFilterChange} 
                  className="w-full sm:w-auto px-2 py-1.5 text-sm rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
                >
                  <option value="">Todas as Extensões</option>
                  {extensoes.map(extensao => (
                    <option key={extensao.idExtensao} value={extensao.idExtensao}>{extensao.nome}</option>
                  ))}
                </select>
                <select 
                  name="status" 
                  value={filters.status} 
                  onChange={handleFilterChange} 
                  className="w-full sm:w-auto px-2 py-1.5 text-sm rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
                >
                  <option value="">Todos os Status</option>
                  <option value="__null__">Sem Status (Nulo)</option>
                  <option value="Aguardando liberação">Aguardando liberação</option>
                  <option value="Ativo">Ativo</option>
                  <option value="Concluido">Concluído</option>
                  <option value="Inativo">Inativo</option>
                  <option value="Rascunho">Rascunho</option>
                </select>
              </div>
            </div>
          )}
          
          {/* Controles de paginação e colunas */}
          <div className="flex flex-col gap-3 mb-4">
            {/* Linha 1: Paginação */}
            <div className="w-full overflow-x-auto">
              <Pagination 
                currentPage={page} 
                totalPages={totalPages} 
                totalItems={totalServer}
                itemsPerPage={pageSize}
                onPageChange={handlePageChange} 
              />
            </div>
            
            {/* Linha 2: Controles (Por página + Colunas) */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <label htmlFor="pageSize" className="text-xs sm:text-sm text-gray-700 whitespace-nowrap">Por página:</label>
                <select
                  id="pageSize"
                  value={pageSize}
                  onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                  className="px-2 py-1.5 text-sm rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
                >
                  {pageSizes.map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
              
              <button
                type="button"
                className="flex items-center justify-center gap-2 px-3 py-1.5 text-sm rounded bg-green-600 text-white font-medium shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400"
                onClick={handleOpenModal}
                aria-label="Exibir/Ocultar colunas"
              >
                <FontAwesomeIcon icon={faEye} />
                <span>Colunas</span>
              </button>
            </div>
          </div>
          
          {/* Tabela com scroll horizontal em mobile */}
          <div className="max-w-full overflow-x-auto">
            {loading ? (
              <div>Carregando alunos...</div>
            ) : error ? (
              <div>Erro: {error.message}</div>
            ) : (
              <Table
                columns={colunasDisponiveis.filter(col => visibleColumns.includes(col.accessor)).map(col => {
                  if (col.accessor === 'nome') {
                    return {
                      ...col,
                      Cell: (value, row) => (
                        <div className="flex items-center gap-2">
                          <span>{value}</span>
                          <Link
                            to={`/alunos/${row.idPessoa}/edit`}
                            className="text-green-600 hover:text-green-800 p-1 rounded focus:outline-none"
                            title="Editar"
                          >
                            <FontAwesomeIcon icon={faPen} />
                          </Link>
                        </div>
                      )
                    };
                  }
                  return col;
                })}
                data={paginatedAlunos}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlunosList;
